/**
 * Partner Application Status Page — Track application progress.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Stepper, Step, StepLabel, Button,
  CircularProgress, Chip,
} from '@mui/material';
import { CheckCircle, HourglassEmpty, Cancel, Storefront } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import partnerService from '../../services/partnerService';
import { colors, designTokens } from '../../theme/theme';

const STAGES = [
  { label: 'Applied', key: 'applied' },
  { label: 'Under Review', key: 'under_review' },
  { label: 'Approved', key: 'approved' },
  { label: 'Active', key: 'active' },
];

function getStepIndex(status: string): number {
  const idx = STAGES.findIndex(s => s.key === status);
  return idx >= 0 ? idx : 0;
}

export default function PartnerApplicationStatusPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    partnerService.getApplicationStatus()
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: colors.background.default }}>
      <CircularProgress />
    </Box>
  );

  if (!data?.status) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: colors.background.default }}>
        <Paper elevation={0} sx={{ p: 5, maxWidth: 400, textAlign: 'center', borderRadius: 5, bgcolor: designTokens.glass.surface, border: `1px solid ${designTokens.glass.border}` }}>
          <Storefront sx={{ fontSize: 48, color: colors.neutral[400], mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFF', mb: 1 }}>{t('pages.partner_apply.no_application')}</Typography>
          <Typography sx={{ color: colors.neutral[400], mb: 3 }}>You haven't submitted a partner application yet.</Typography>
          <Button variant="contained" onClick={() => navigate('/partner/apply')} sx={{ borderRadius: 3 }}>Apply Now</Button>
        </Paper>
      </Box>
    );
  }

  const isRejected = data.status === 'rejected';
  const activeStep = isRejected ? -1 : getStepIndex(data.status);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: colors.background.default, py: 4 }}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#FFF' }}>{t('pages.partner_apply.status_title')}</Typography>
          <Typography sx={{ color: colors.neutral[400] }}>{data.venue_name}</Typography>
        </Box>

        {isRejected ? (
          <Paper elevation={0} sx={{ p: 4, borderRadius: 4, bgcolor: designTokens.glass.surface, border: `1px solid ${colors.error.main}40`, textAlign: 'center' }}>
            <Cancel sx={{ fontSize: 56, color: colors.error.main, mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFF', mb: 1 }}>Application Not Approved</Typography>
            <Typography sx={{ color: colors.neutral[400], mb: 3 }}>
              Unfortunately, we're unable to approve your application at this time. If you have questions, please contact our support team.
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/')} sx={{ borderRadius: 3, color: colors.neutral[400] }}>Back to Home</Button>
          </Paper>
        ) : (
          <>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4,
              '& .MuiStepLabel-label': { color: colors.neutral[500] },
              '& .Mui-active .MuiStepLabel-label': { color: '#FFF', fontWeight: 700 },
              '& .Mui-completed .MuiStepLabel-label': { color: colors.success.main },
            }}>
              {STAGES.map(s => (
                <Step key={s.key}>
                  <StepLabel>{s.label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Paper elevation={0} sx={{ p: 4, borderRadius: 4, bgcolor: designTokens.glass.surface, border: `1px solid ${designTokens.glass.border}`, textAlign: 'center' }}>
              {data.status === 'active' ? (
                <>
                  <CheckCircle sx={{ fontSize: 56, color: colors.success.main, mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFF', mb: 1 }}>You're Live!</Typography>
                  <Typography sx={{ color: colors.neutral[400], mb: 3 }}>Your venue is active on CoffeeMates. Start hosting events!</Typography>
                  <Button variant="contained" onClick={() => navigate('/partner')} sx={{ borderRadius: 3 }}>Go to Partner Portal</Button>
                </>
              ) : (
                <>
                  <HourglassEmpty sx={{ fontSize: 56, color: colors.warning.main, mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFF', mb: 1 }}>
                    {data.status === 'under_review' ? 'Under Review' : data.status === 'approved' ? 'Almost There!' : 'Application Received'}
                  </Typography>
                  <Typography sx={{ color: colors.neutral[400], mb: 2 }}>
                    {data.status === 'under_review'
                      ? 'An admin is reviewing your application. We\'ll notify you of the outcome.'
                      : data.status === 'approved'
                      ? 'Your application is approved! Activation is in progress.'
                      : 'We\'ll review your application within 48 hours and keep you updated.'}
                  </Typography>
                  {data.submitted_at && (
                    <Chip label={`Submitted: ${new Date(data.submitted_at).toLocaleDateString()}`} size="small"
                      sx={{ color: colors.neutral[400], borderColor: designTokens.glass.border }} variant="outlined" />
                  )}
                </>
              )}
            </Paper>
          </>
        )}
      </Container>
    </Box>
  );
}
